import React, { useState } from 'react';
import {
    Avatar,
    Button,
    Container,
    Grid,
    TextField,
    Typography,
    Paper,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { ProfileStyles } from './ProfilePageStyles';
import { useDispatch, useSelector } from "react-redux";
import { rolesDescription } from "./RolesDescription";
import usePostItem from "../../Hooks/usePostItem";
import { UPDATE_USER, CHECK_PASSWORD } from "@API/APIRoutes";
import { ChangePasswordSchema, NameValidation } from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import { setUser } from "@Redux/userSlice";
import LoadingComponent from "../../Components/LoadingComponent/LoadingComponent";
import { validateField, validateForm } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import SwitchButton from "@Components/SwitchButton/SwitchButton";

const Profile = () => {
    const user = useSelector((state) => state.user);
    const [formData, setFormData] = useState({
        username: user.username,
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
    });

    const dispatch = useDispatch();
    const description = rolesDescription(user.role);

    const { loading: checkingPasswordMatch, postItem: checkPasswordMatch } = usePostItem();
    const { loading: updatingUser, postItem: updateUser } = usePostItem();

    const [matched, setMatched] = useState(false);
    const [errors, setErrors] = useState({});

    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmNewPassword: false
    });

    // New state to control enabling/disabling password change fields.
    const [enablePasswordChange, setEnablePasswordChange] = useState(false);

    const handleTogglePassword = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Handler for the "Change Password" toggle switch.
    const handleTogglePasswordChange = (event) => {
        const enabled = event.target.checked;
        setEnablePasswordChange(enabled);
        if (!enabled) {
            // Clear all password fields and reset matching when turning off password change.
            setFormData(prev => ({
                ...prev,
                currentPassword: "",
                newPassword: "",
                confirmNewPassword: "",
            }));
            setMatched(false);
            setErrors(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            }));
        }
    };

    // Extend the base schema with a username validation field.
    const validationSchema = ChangePasswordSchema(matched).keys({
        username: NameValidation("Username")
    });

    // Check current password with the backend when field loses focus.
    const checkPassword = () => {
        setErrors(prev => ({ ...prev, currentPassword: '' }));
        if (formData.currentPassword.trim() !== "") {
            checkPasswordMatch(
                CHECK_PASSWORD,
                { password: formData.currentPassword },
                checkPasswordMatchCallback,
                '',
                "",
                false,
            );
        }
    };

    const checkPasswordMatchCallback = (response) => {
        if (response && response.matches) {
            setMatched(true);
            setErrors(prev => ({ ...prev, currentPassword: '' }));
        } else {
            setMatched(false);
            setErrors(prev => ({ ...prev, currentPassword: 'Current password does not match' }));
        }
    };

    const handleOnBlur = (field, value) => {
            validateField(field, value, setFormData, formData, validationSchema, setErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'currentPassword') {
            setMatched(false);
            // Reset new and confirm password fields whenever current password changes.
            setFormData(prev => ({
                ...prev,
                currentPassword: value.trim() === '' ? '' : value,
                newPassword: '',
                confirmNewPassword: ''
            }));

            setErrors(prev => ({
                ...prev,
                currentPassword: value.trim() === '' ? '' : prev.currentPassword,
                newPassword: '',
                confirmNewPassword: ''
            }));
        }
        else {
            setErrors({
                ...errors,
                [name]: ''
            })
        }
    };

    const updateUserCallback = (response) => {
        dispatch(
            setUser({
                ...user,
                username: formData.username,
            })
        );
        setFormData({
            ...formData,
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
        });
        setMatched(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm(formData,setErrors, validationSchema)) {
            let payload = {
                username: formData.username,
                password: null
            };

            if (enablePasswordChange && formData.currentPassword !== "" && matched) {
                payload.password = formData.newPassword;
            }
            updateUser(UPDATE_USER, payload, updateUserCallback, 'Profile Updated Successfully', 'Error While Updating Profile', true, "put");
        }
    };

    return (
        <ProfileStyles>
            <Container maxWidth="lg" className="container">
                <Paper elevation={3} className="paper">
                    <Grid container spacing={3}>
                        {/* Left Profile Section */}
                        <Grid item xs={12} md={2.5} className="profileSection">
                            <div className="profileContent">
                                <Avatar src="/profileAvatarLight.jpg" className="avatar" />
                                <Typography variant="h6" className="profileName">
                                    {user.username}
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    {user.email}
                                </Typography>
                            </div>
                        </Grid>
                        {/* Middle Form Section */}
                        <Grid item xs={12} md={6} className="formSection">
                            <Typography variant="h4" gutterBottom className="sectionTitle">
                                Profile Settings
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} className="textField">
                                        <TextField
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            placeholder="Enter Username"
                                            fullWidth
                                            label="Username"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            variant="outlined"
                                            error={Boolean(errors.username)}
                                            helperText={errors.username}
                                            onBlur={(e) => {
                                                handleOnBlur("username", e.target.value);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} className="textField">
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={user.email}
                                            variant="outlined"
                                            disabled
                                        />
                                    </Grid>
                                    {/* Change Password Section with Toggle */}
                                    <Grid item xs={12} className="passwordSection" container alignItems="center" justifyContent="space-between">
                                        <Typography variant="h6" gutterBottom className="passwordSectionHeader">
                                            Change Password
                                        </Typography>
                                        <SwitchButton
                                            checked={enablePasswordChange}
                                            onChange={handleTogglePasswordChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} className="textField">
                                        <TextField
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            label={"Current Password"}
                                            placeholder={enablePasswordChange ? "Enter Current Password" : "Enable Change Password To Enter"}
                                            name="currentPassword"
                                            value={enablePasswordChange ?formData.currentPassword: ""}
                                            onChange={handleChange}
                                            onBlur={enablePasswordChange ? checkPassword : null}
                                            variant="outlined"
                                            error={Boolean(errors.currentPassword)}
                                            helperText={
                                                enablePasswordChange && checkingPasswordMatch ? (
                                                    <span style={{ color: 'grey' }}>
                                                        Verifying Password Match ...
                                                        <LoadingComponent size="1em" thickness={5} color={"black"} />
                                                    </span>
                                                ) : enablePasswordChange && matched ? (
                                                    <span style={{ color: 'green' }}>✔ Password Matched</span>
                                                ) : (
                                                    enablePasswordChange && errors.currentPassword
                                                )
                                            }
                                            type={showPassword.currentPassword ? "text" : "password"}
                                            disabled={!enablePasswordChange}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => handleTogglePassword("currentPassword")} edge="end">
                                                            {showPassword.currentPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} className="textField">
                                        <TextField
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label="New Password"
                                            name="newPassword"
                                            placeholder={enablePasswordChange ? "Enter New Password" : "Enable Change Password"}
                                            value={formData.newPassword}
                                            onChange={handleChange}
                                            variant="outlined"
                                            disabled={!enablePasswordChange || !matched}
                                            error={Boolean(errors.newPassword)}
                                            helperText={errors.newPassword}
                                            onBlur={(e) => {
                                                enablePasswordChange && handleOnBlur("newPassword", e.target.value);
                                            }}
                                            type={showPassword.newPassword ? "text" : "password"}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => handleTogglePassword("newPassword")} edge="end">
                                                            {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} className="textField">
                                        <TextField
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            fullWidth
                                            label="Confirm New Password"
                                            name="confirmNewPassword"
                                            placeholder={enablePasswordChange ? "Re-enter New Password" : "Enable Change Password"}
                                            value={formData.confirmNewPassword}
                                            onChange={handleChange}
                                            variant="outlined"
                                            disabled={!enablePasswordChange || !matched}
                                            error={Boolean(errors.confirmNewPassword)}
                                            helperText={errors.confirmNewPassword}
                                            onBlur={(e) => {
                                                enablePasswordChange && handleOnBlur("confirmNewPassword", e.target.value);
                                            }}
                                            type={showPassword.confirmNewPassword ? "text" : "password"}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => handleTogglePassword("confirmNewPassword")} edge="end">
                                                            {showPassword.confirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    className="saveButton"
                                    disabled={
                                        updatingUser ||
                                        checkingPasswordMatch ||
                                        // If password change is enabled, ensure a valid current password match.
                                        (enablePasswordChange && !matched) ||
                                        // If not updating password and the username is unchanged.
                                        (!enablePasswordChange && formData.username.trim() === user.username)
                                    }
                                >
                                    {updatingUser?(
                                        <LoadingComponent size="2em" thickness={5}  />
                                    ):(
                                        "Save Profile"
                                    )
                                    }
                                </Button>
                            </form>
                        </Grid>
                        {/* Right Experience Section */}
                        <Grid item xs={12} md={3.5} className="roleSection">
                            <div className="experienceContent">
                                <Typography variant="h5" className="roleHeader">
                                    Role Overview
                                </Typography>

                                {/* Region and Categories Section - Only for non-ADMIN users */}
                                {user.role !== 'ADMIN' && (
                                    <>
                                        {user.regions && user.regions.length > 0 && (
                                            (user.role === 'APPROVER' || user.role === 'RD_MANAGER') && user.regions.length > 1 ? (
                                                <Paper className="categoriesPaper">
                                                    <Typography variant="body2" className="infoLabel">
                                                        Regions
                                                    </Typography>
                                                    <Typography variant="body1" className="categoriesValue">
                                                        {user.regions.join(', ')}
                                                    </Typography>
                                                </Paper>
                                            ) : (
                                                <Paper className="infoPaper">
                                                    <Typography variant="body2" className="infoLabel">
                                                        Region
                                                    </Typography>
                                                    <Typography variant="body1" className="infoValue">
                                                        {user.regions[0]}
                                                    </Typography>
                                                </Paper>
                                            )
                                        )}

                                        {(user.role === 'R&D' || user.role === 'RD_MANAGER') && user.categories && user.categories.length > 0 && (
                                            <Paper className="categoriesPaper">
                                                <Typography variant="body2" className="infoLabel">
                                                    Categories
                                                </Typography>
                                                <Typography variant="body1" className="categoriesValue">
                                                    {user.categories.join(', ')}
                                                </Typography>
                                            </Paper>
                                        )}
                                    </>
                                )}

                                <Paper className="infoPaper">
                                    <Typography variant="body2" className="infoLabel">
                                        Role
                                    </Typography>
                                    <Typography variant="body1" className="infoValue">
                                        {user.role}
                                    </Typography>
                                </Paper>

                                <Typography variant="h6" className="roleSubHeader">
                                    System Privileges
                                </Typography>
                                <Typography variant="body1">
                                    {description}
                                </Typography>
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </ProfileStyles>
    );
};

export default Profile;
